import { A } from "@solidjs/router";
import { Component, For, JSX } from "solid-js";
import { IconChevronRight } from "@paulie/ui";

export interface NavCardTag {
  /** Tag display text */
  label: string;
  /** Tailwind classes for border, background, and text color */
  class: string;
}

export interface NavCardProps {
  /** Link destination URL */
  href: string;
  /** Opens in new tab with noopener noreferrer */
  external?: boolean;
  /** Icon element (emoji span or SVG) */
  icon: JSX.Element;
  /** Card title */
  title: string;
  /** Card description */
  description: string;
  /** Tags with individual color classes */
  tags: NavCardTag[];
  /** Tailwind classes for border, gradient, and hover on the card wrapper */
  cardClass: string;
  /** Tailwind classes for title text color and hover */
  titleClass: string;
  /** Tailwind class for chevron color */
  chevronClass: string;
}

/**
 * NavCard - A navigational card with icon, title, description, tags, and chevron.
 * Supports both internal routes (SolidJS router) and external links.
 */
export const NavCard: Component<NavCardProps> = (props) => {
  const content = () => (
    <div class="flex items-center justify-between">
      <div class="space-y-3">
        <div class="flex items-center gap-3">
          {props.icon}
          <h3
            class={`text-2xl font-bold transition-colors ${props.titleClass}`}
          >
            {props.title}
          </h3>
        </div>
        <p class="text-sm text-gray-400 transition-colors group-hover:text-gray-300">
          {props.description}
        </p>
        <div class="flex flex-wrap gap-2 text-xs">
          <For each={props.tags}>
            {(tag) => (
              <span class={`rounded border px-2 py-1 ${tag.class}`}>
                {tag.label}
              </span>
            )}
          </For>
        </div>
      </div>
      <IconChevronRight
        class={`h-8 w-8 flex-shrink-0 transition-transform group-hover:translate-x-2 ${props.chevronClass}`}
      />
    </div>
  );

  const baseClass = () =>
    `group block rounded-lg border p-4 transition-all duration-200 sm:p-8 ${props.cardClass}`;

  return props.external ? (
    <a
      href={props.href}
      target="_blank"
      rel="noopener noreferrer"
      class={baseClass()}
    >
      {content()}
    </a>
  ) : (
    <A href={props.href} class={baseClass()}>
      {content()}
    </A>
  );
};
